import {
  APPLICATION_BLOCK_PATTERN_PROFILES,
  APPLICATION_LAYOUT_PATTERN_PROFILES,
  APPLICATION_PAGE_PATTERN_PROFILES,
  FIXED_COMPONENT_ONTOLOGY_COMPONENT_TYPES,
  type BlockPatternProfile,
  type LayoutPatternProfile,
  type PagePatternProfile,
} from "./application-composition-methodology";

export const APPLICATION_ONTOLOGY_MATCHING_SCHEMA_VERSION =
  "cogo.application-ontology-matching.v1";

export type OntologyMatchingAssetKind = "page" | "ui-block" | "component";
export type OntologyMatchingDecisionKind =
  | "reuse"
  | "adapt"
  | "compose"
  | "create_candidate";
export type OntologyMatchingAuthority = "tool" | "llm-advisory";

export interface OntologyIntentChunk {
  id: string;
  text: string;
  weight?: number;
  tags?: readonly string[];
}

export interface OntologyMatchingAsset {
  id: string;
  kind: OntologyMatchingAssetKind;
  label?: string;
  description?: string;
  tags?: readonly string[];
  ontologyRefs?: readonly string[];
  chunkHandles?: readonly string[];
  designSystemIds?: readonly string[];
  templateIds?: readonly string[];
  requiredChildIds?: readonly string[];
  optionalChildIds?: readonly string[];
  requiredComponents?: readonly string[];
  actionOutputs?: readonly string[];
  dataInputs?: readonly string[];
}

export interface OntologyMatchingLlmAliasPatch {
  term: string;
  expandsTo: readonly string[];
  confidence: number;
  reason: string;
}

export interface OntologyMatchingLlmWeightPatch {
  term: string;
  weight: number;
  reason: string;
}

export interface OntologyMatchingLlmSearchWindowPatch {
  pageTopK?: number;
  uiBlockTopK?: number;
  componentTopK?: number;
  minScore?: number;
  reason: string;
}

export interface OntologyMatchingLlmPatch {
  aliases?: readonly OntologyMatchingLlmAliasPatch[];
  weights?: readonly OntologyMatchingLlmWeightPatch[];
  searchWindow?: OntologyMatchingLlmSearchWindowPatch;
  prohibitedAssetIds?: readonly string[];
}

export interface OntologyMatchingSelectedAuthority {
  designSystemId?: string | null;
  templateId?: string | null;
  requiredPageIds?: readonly string[];
  requiredUiBlockIds?: readonly string[];
  requiredComponentIds?: readonly string[];
}

export interface OntologyMatchingSearchLevel {
  kind: OntologyMatchingAssetKind;
  topK: number;
  minScore: number;
  reason: string;
}

export interface OntologyMatchingCandidateScore {
  assetId: string;
  kind: OntologyMatchingAssetKind;
  score: number;
  lexicalScore: number;
  authorityScore: number;
  structuralScore: number;
  reasons: string[];
}

export interface OntologyMatchingDecision {
  requirementId: string;
  kind: OntologyMatchingAssetKind;
  decision: OntologyMatchingDecisionKind;
  authority: OntologyMatchingAuthority;
  selectedAssetId?: string;
  score: number;
  fallbackFrom?: OntologyMatchingAssetKind;
  generationAllowed: boolean;
  candidateLifecycleStatus:
    | "admitted"
    | "adaptation-required"
    | "composed"
    | "candidate";
  reasons: string[];
  evidenceRefs: string[];
}

export interface OntologyMatchingActionFlowImpact {
  required: boolean;
  mode: "none" | "patch" | "rebuild";
  impactedAssetIds: string[];
  requiredValidation: readonly string[];
  reasons: string[];
}

export interface OntologyMatchingPlan {
  schemaVersion: typeof APPLICATION_ONTOLOGY_MATCHING_SCHEMA_VERSION;
  intentChunks: OntologyIntentChunk[];
  normalizedIntentTerms: string[];
  acceptedLlmPatches: Array<{
    type: "alias" | "weight" | "search-window" | "prohibition";
    reason: string;
  }>;
  rejectedLlmPatches: Array<{
    type: "alias" | "weight" | "search-window" | "prohibition";
    reason: string;
  }>;
  hardConstraints: string[];
  searchWindow: OntologyMatchingSearchLevel[];
  candidateScores: OntologyMatchingCandidateScore[];
  decisions: OntologyMatchingDecision[];
  actionFlowImpact: OntologyMatchingActionFlowImpact;
}

export interface BuildOntologyMatchingPlanInput {
  intent: string;
  chunks?: readonly OntologyIntentChunk[];
  assets?: readonly OntologyMatchingAsset[];
  selected?: OntologyMatchingSelectedAuthority;
  llmPatch?: OntologyMatchingLlmPatch;
}

type TokenWeights = Map<string, number>;

const DEFAULT_MIN_SCORE = 0.38;
const REUSE_THRESHOLD = 0.72;
const ADAPT_THRESHOLD = 0.46;
const MAX_TOP_K = {
  page: 5,
  "ui-block": 12,
  component: 24,
} as const;

const GENERIC_STOP_WORDS = new Set([
  "a",
  "an",
  "and",
  "app",
  "application",
  "build",
  "create",
  "for",
  "from",
  "in",
  "of",
  "the",
  "to",
  "with",
  "사용자",
  "앱",
  "생성",
]);

export function buildOntologyMatchingPlan(
  input: BuildOntologyMatchingPlanInput,
): OntologyMatchingPlan {
  const chunks = normalizeIntentChunks(input.intent, input.chunks);
  const selected = input.selected ?? {};
  const assets = normalizeAssets(
    input.assets ?? buildDefaultOntologyMatchingAssets(),
  );
  const patchReview = reviewLlmPatch(input.llmPatch);
  const tokenWeights = buildTokenWeights(
    chunks,
    patchReview.acceptedAliases,
    patchReview.acceptedWeights,
  );
  const normalizedIntentTerms = [...tokenWeights.keys()].sort();
  const hardConstraints = buildHardConstraints(selected);
  const prohibitionReview = applyLlmProhibitions(
    patchReview.acceptedProhibitedAssetIds,
    assets,
    selected,
  );
  const prohibitedIds = new Set(prohibitionReview.prohibitedIds);
  const candidates = assets
    .filter(
      (asset) =>
        passesHardScope(asset, selected) && !prohibitedIds.has(asset.id),
    )
    .map((asset) => scoreAsset(asset, tokenWeights, selected, assets))
    .sort(compareCandidateScores);
  const searchWindow = buildSearchWindow(
    candidates,
    patchReview.searchWindow,
    selected,
  );
  const decisions = buildDecisions(candidates, assets, selected, searchWindow);
  const actionFlowImpact = buildActionFlowImpact(decisions, assets);

  return {
    schemaVersion: APPLICATION_ONTOLOGY_MATCHING_SCHEMA_VERSION,
    intentChunks: chunks,
    normalizedIntentTerms,
    acceptedLlmPatches: [
      ...patchReview.accepted,
      ...prohibitionReview.accepted,
    ],
    rejectedLlmPatches: [
      ...patchReview.rejected,
      ...prohibitionReview.rejected,
    ],
    hardConstraints,
    searchWindow,
    candidateScores: candidates,
    decisions,
    actionFlowImpact,
  };
}

export function buildDefaultOntologyMatchingAssets(): OntologyMatchingAsset[] {
  const layouts = new Map(
    APPLICATION_LAYOUT_PATTERN_PROFILES.map((layout) => [
      layout.patternId,
      layout,
    ]),
  );
  const blocks = new Map(
    APPLICATION_BLOCK_PATTERN_PROFILES.map((block) => [block.patternId, block]),
  );
  const pageAssets = APPLICATION_PAGE_PATTERN_PROFILES.map((page) =>
    pagePatternToAsset(page, layouts),
  );
  const blockAssets = APPLICATION_BLOCK_PATTERN_PROFILES.map((block) =>
    blockPatternToAsset(block, layouts),
  );
  const componentAssets = FIXED_COMPONENT_ONTOLOGY_COMPONENT_TYPES.map(
    (component) => ({
      id: component,
      kind: "component" as const,
      label: component,
      description: `Fixed Cogo component ontology type ${component}.`,
      tags: splitTerms(component),
      ontologyRefs: [`component:${component}`],
    }),
  );
  for (const block of blockAssets) {
    const profile = blocks.get(block.id);
    if (!profile) continue;
    block.requiredComponents = profile.requiredComponents;
  }
  return [...pageAssets, ...blockAssets, ...componentAssets];
}

function pagePatternToAsset(
  page: PagePatternProfile,
  layouts: ReadonlyMap<string, LayoutPatternProfile>,
): OntologyMatchingAsset {
  return {
    id: page.patternId,
    kind: "page",
    label: page.patternId,
    description: [
      page.routeScope,
      page.stateScope,
      page.actionScope,
      ...page.layoutPatternRefs.map(
        (ref) => layouts.get(ref)?.applicableContentModes.join(" ") ?? ref,
      ),
    ].join(" "),
    tags: [
      ...splitTerms(page.patternId),
      ...splitTerms(page.routeScope),
      ...splitTerms(page.actionScope),
    ],
    ontologyRefs: [`page-pattern:${page.patternId}`],
    requiredChildIds: page.requiredBlockPatternRefs,
    optionalChildIds: page.optionalBlockPatternRefs,
    actionOutputs: [page.actionScope],
    dataInputs: [page.stateScope],
  };
}

function blockPatternToAsset(
  block: BlockPatternProfile,
  layouts: ReadonlyMap<string, LayoutPatternProfile>,
): OntologyMatchingAsset {
  return {
    id: block.patternId,
    kind: "ui-block",
    label: block.patternId,
    description: [
      block.purpose,
      ...block.dataInputs,
      ...block.actionOutputs,
      ...block.layoutPatternRefs.map(
        (ref) => layouts.get(ref)?.applicableContentModes.join(" ") ?? ref,
      ),
    ].join(" "),
    tags: [
      ...splitTerms(block.patternId),
      ...splitTerms(block.purpose),
      ...block.requiredComponents,
      ...block.optionalComponents,
    ],
    ontologyRefs: [`block-pattern:${block.patternId}`],
    requiredChildIds: block.requiredComponents,
    requiredComponents: block.requiredComponents,
    optionalChildIds: block.optionalComponents,
    actionOutputs: block.actionOutputs,
    dataInputs: block.dataInputs,
  };
}

function normalizeIntentChunks(
  intent: string,
  chunks: readonly OntologyIntentChunk[] | undefined,
): OntologyIntentChunk[] {
  const provided = (chunks ?? []).filter(
    (chunk) => chunk.text.trim().length > 0,
  );
  if (provided.length > 0) {
    return provided.map((chunk, index) => ({
      ...chunk,
      id: chunk.id.trim() || `chunk-${index + 1}`,
      text: chunk.text.trim(),
      weight: clamp(chunk.weight ?? 1, 0.1, 5),
    }));
  }
  return [
    {
      id: "user-intent",
      text: intent.trim(),
      weight: 1,
    },
  ];
}

function normalizeAssets(
  assets: readonly OntologyMatchingAsset[],
): OntologyMatchingAsset[] {
  const seen = new Set<string>();
  return assets.flatMap((asset) => {
    if (!asset.id.trim() || seen.has(asset.id)) return [];
    seen.add(asset.id);
    return [
      {
        ...asset,
        label: asset.label ?? asset.id,
        tags: [
          ...new Set([
            ...(asset.tags ?? []),
            ...splitTerms(asset.id),
            ...splitTerms(asset.label ?? ""),
          ]),
        ],
        ontologyRefs: asset.ontologyRefs ?? [`${asset.kind}:${asset.id}`],
      },
    ];
  });
}

function reviewLlmPatch(patch: OntologyMatchingLlmPatch | undefined): {
  acceptedAliases: OntologyMatchingLlmAliasPatch[];
  acceptedWeights: OntologyMatchingLlmWeightPatch[];
  acceptedProhibitedAssetIds: string[];
  searchWindow?: OntologyMatchingLlmSearchWindowPatch;
  accepted: OntologyMatchingPlan["acceptedLlmPatches"];
  rejected: OntologyMatchingPlan["rejectedLlmPatches"];
} {
  const accepted: OntologyMatchingPlan["acceptedLlmPatches"] = [];
  const rejected: OntologyMatchingPlan["rejectedLlmPatches"] = [];
  const acceptedAliases: OntologyMatchingLlmAliasPatch[] = [];
  const acceptedWeights: OntologyMatchingLlmWeightPatch[] = [];
  const acceptedProhibitedAssetIds: string[] = [];

  for (const alias of patch?.aliases ?? []) {
    const term = normalizeToken(alias.term);
    const expansions = alias.expandsTo.map(normalizeToken).filter(Boolean);
    if (!term || expansions.length === 0 || alias.confidence < 0.55) {
      rejected.push({
        type: "alias",
        reason: `Rejected weak alias "${alias.term}".`,
      });
      continue;
    }
    acceptedAliases.push({ ...alias, term, expandsTo: expansions });
    accepted.push({ type: "alias", reason: alias.reason });
  }

  for (const weight of patch?.weights ?? []) {
    const term = normalizeToken(weight.term);
    if (!term || weight.weight <= 0 || weight.weight > 5) {
      rejected.push({
        type: "weight",
        reason: `Rejected unsafe weight for "${weight.term}".`,
      });
      continue;
    }
    acceptedWeights.push({
      ...weight,
      term,
      weight: clamp(weight.weight, 0.1, 5),
    });
    accepted.push({ type: "weight", reason: weight.reason });
  }

  for (const assetId of patch?.prohibitedAssetIds ?? []) {
    if (!assetId.trim()) {
      rejected.push({
        type: "prohibition",
        reason: "Rejected blank prohibited asset id.",
      });
      continue;
    }
    acceptedProhibitedAssetIds.push(assetId);
  }

  let searchWindow = patch?.searchWindow;
  if (searchWindow) {
    const unsafe =
      (searchWindow.minScore !== undefined &&
        (searchWindow.minScore < 0.2 || searchWindow.minScore > 0.9)) ||
      (searchWindow.pageTopK !== undefined &&
        searchWindow.pageTopK > MAX_TOP_K.page) ||
      (searchWindow.uiBlockTopK !== undefined &&
        searchWindow.uiBlockTopK > MAX_TOP_K["ui-block"]) ||
      (searchWindow.componentTopK !== undefined &&
        searchWindow.componentTopK > MAX_TOP_K.component);
    if (unsafe) {
      rejected.push({
        type: "search-window",
        reason: "Rejected search-window patch outside deterministic bounds.",
      });
      searchWindow = undefined;
    } else {
      accepted.push({ type: "search-window", reason: searchWindow.reason });
    }
  }

  return {
    acceptedAliases,
    acceptedWeights,
    acceptedProhibitedAssetIds,
    searchWindow,
    accepted,
    rejected,
  };
}

function applyLlmProhibitions(
  proposedIds: readonly string[],
  assets: readonly OntologyMatchingAsset[],
  selected: OntologyMatchingSelectedAuthority,
): {
  prohibitedIds: string[];
  accepted: OntologyMatchingPlan["acceptedLlmPatches"];
  rejected: OntologyMatchingPlan["rejectedLlmPatches"];
} {
  const assetById = new Map(assets.map((asset) => [asset.id, asset]));
  const accepted: OntologyMatchingPlan["acceptedLlmPatches"] = [];
  const rejected: OntologyMatchingPlan["rejectedLlmPatches"] = [];
  const prohibitedIds: string[] = [];
  for (const assetId of proposedIds) {
    const asset = assetById.get(assetId);
    if (isHardSelectedAsset(assetId, asset, selected)) {
      rejected.push({
        type: "prohibition",
        reason: `Rejected LLM prohibition for hard-selected asset ${assetId}.`,
      });
      continue;
    }
    prohibitedIds.push(assetId);
    accepted.push({
      type: "prohibition",
      reason: `LLM suggested excluding ${assetId}.`,
    });
  }
  return { prohibitedIds, accepted, rejected };
}

function isHardSelectedAsset(
  assetId: string,
  asset: OntologyMatchingAsset | undefined,
  selected: OntologyMatchingSelectedAuthority,
): boolean {
  if (selected.requiredPageIds?.includes(assetId)) return true;
  if (selected.requiredUiBlockIds?.includes(assetId)) return true;
  if (selected.requiredComponentIds?.includes(assetId)) return true;
  if (selected.templateId && asset?.templateIds?.includes(selected.templateId))
    return true;
  if (
    selected.designSystemId &&
    asset?.designSystemIds?.includes(selected.designSystemId)
  )
    return true;
  return false;
}

function passesHardScope(
  asset: OntologyMatchingAsset,
  selected: OntologyMatchingSelectedAuthority,
): boolean {
  if (
    selected.templateId &&
    asset.templateIds?.length &&
    !asset.templateIds.includes(selected.templateId)
  ) {
    return false;
  }
  if (
    selected.designSystemId &&
    asset.designSystemIds?.length &&
    !asset.designSystemIds.includes(selected.designSystemId)
  ) {
    return false;
  }
  return true;
}

function buildTokenWeights(
  chunks: readonly OntologyIntentChunk[],
  aliases: readonly OntologyMatchingLlmAliasPatch[],
  weights: readonly OntologyMatchingLlmWeightPatch[],
): TokenWeights {
  const tokenWeights: TokenWeights = new Map();
  for (const chunk of chunks) {
    const baseWeight = clamp(chunk.weight ?? 1, 0.1, 5);
    for (const token of tokenize(
      [chunk.text, ...(chunk.tags ?? [])].join(" "),
    )) {
      tokenWeights.set(token, (tokenWeights.get(token) ?? 0) + baseWeight);
    }
  }
  for (const alias of aliases) {
    const sourceWeight = tokenWeights.get(alias.term) ?? 1;
    for (const expansion of alias.expandsTo) {
      tokenWeights.set(
        expansion,
        Math.max(
          tokenWeights.get(expansion) ?? 0,
          sourceWeight * alias.confidence,
        ),
      );
    }
  }
  for (const weight of weights) {
    tokenWeights.set(
      weight.term,
      Math.max(tokenWeights.get(weight.term) ?? 0, weight.weight),
    );
  }
  return tokenWeights;
}

function scoreAsset(
  asset: OntologyMatchingAsset,
  intentWeights: ReadonlyMap<string, number>,
  selected: OntologyMatchingSelectedAuthority,
  assets: readonly OntologyMatchingAsset[],
): OntologyMatchingCandidateScore {
  const searchable = tokenize(
    [
      asset.id,
      asset.label,
      asset.description,
      ...(asset.tags ?? []),
      ...(asset.ontologyRefs ?? []),
      ...(asset.chunkHandles ?? []),
      ...(asset.actionOutputs ?? []),
      ...(asset.dataInputs ?? []),
    ]
      .filter(Boolean)
      .join(" "),
  );
  const searchableSet = new Set(searchable);
  const matchedWeights = [...intentWeights.entries()]
    .filter(([token]) => searchableSet.has(token))
    .reduce((sum, [, weight]) => sum + weight, 0);
  const totalWeights =
    [...intentWeights.values()].reduce((sum, weight) => sum + weight, 0) || 1;
  const lexicalScore = clamp(matchedWeights / totalWeights, 0, 1);
  const authorityScore = authorityCompatibility(asset, selected);
  const structuralScore = structuralCompleteness(asset, assets);
  const rawScore = clamp(
    lexicalScore * 0.62 + authorityScore * 0.28 + structuralScore * 0.1,
    0,
    1,
  );
  const score = Math.max(rawScore, authorityScoreFloor(asset, selected));
  const reasons = [
    ...(lexicalScore > 0 ? [`lexical=${lexicalScore.toFixed(2)}`] : []),
    ...(authorityScore > 0 ? [`authority=${authorityScore.toFixed(2)}`] : []),
    ...(structuralScore > 0
      ? [`structural=${structuralScore.toFixed(2)}`]
      : []),
  ];
  return {
    assetId: asset.id,
    kind: asset.kind,
    score,
    lexicalScore,
    authorityScore,
    structuralScore,
    reasons,
  };
}

function authorityCompatibility(
  asset: OntologyMatchingAsset,
  selected: OntologyMatchingSelectedAuthority,
): number {
  let score = 0;
  if (selected.designSystemId) {
    if (asset.designSystemIds?.includes(selected.designSystemId)) score += 0.5;
    else if (asset.designSystemIds && asset.designSystemIds.length > 0)
      score -= 0.5;
  }
  if (selected.templateId) {
    if (asset.templateIds?.includes(selected.templateId)) score += 0.5;
    else if (asset.templateIds && asset.templateIds.length > 0) score -= 0.5;
  }
  if (selected.requiredPageIds?.includes(asset.id)) score = 1;
  if (selected.requiredUiBlockIds?.includes(asset.id)) score = 1;
  if (selected.requiredComponentIds?.includes(asset.id)) score = 1;
  return clamp(score, 0, 1);
}

function authorityScoreFloor(
  asset: OntologyMatchingAsset,
  selected: OntologyMatchingSelectedAuthority,
): number {
  if (selected.requiredPageIds?.includes(asset.id)) return 0.96;
  if (selected.requiredUiBlockIds?.includes(asset.id)) return 0.96;
  if (selected.requiredComponentIds?.includes(asset.id)) return 0.96;
  if (selected.templateId && asset.templateIds?.includes(selected.templateId))
    return 0.82;
  if (
    selected.designSystemId &&
    asset.designSystemIds?.includes(selected.designSystemId)
  )
    return 0.74;
  return 0;
}

function structuralCompleteness(
  asset: OntologyMatchingAsset,
  assets: readonly OntologyMatchingAsset[],
): number {
  const required = asset.requiredChildIds ?? asset.requiredComponents ?? [];
  if (required.length === 0) return 0.35;
  const assetIds = new Set(assets.map((entry) => entry.id));
  const present = required.filter((id) => assetIds.has(id)).length;
  return present / required.length;
}

function buildSearchWindow(
  candidates: readonly OntologyMatchingCandidateScore[],
  patch: OntologyMatchingLlmSearchWindowPatch | undefined,
  selected: OntologyMatchingSelectedAuthority,
): OntologyMatchingSearchLevel[] {
  const topPage = candidates.find((candidate) => candidate.kind === "page");
  const topBlock = candidates.find(
    (candidate) => candidate.kind === "ui-block",
  );
  const minScore = patch?.minScore ?? DEFAULT_MIN_SCORE;
  const templateForced = Boolean(selected.templateId);
  if (templateForced) {
    return [
      {
        kind: "page",
        topK: patch?.pageTopK ?? 3,
        minScore,
        reason: "selected template keeps page scope active",
      },
      {
        kind: "ui-block",
        topK: patch?.uiBlockTopK ?? 8,
        minScore,
        reason: "selected template requires block allowlist check",
      },
      {
        kind: "component",
        topK: patch?.componentTopK ?? 12,
        minScore,
        reason: "selected template requires component allowlist check",
      },
    ];
  }
  if (!topPage || topPage.score < minScore) {
    return [
      {
        kind: "ui-block",
        topK: patch?.uiBlockTopK ?? 10,
        minScore: minScore * 0.9,
        reason: "page ontology not confident; compose page from ui-blocks",
      },
      {
        kind: "component",
        topK: patch?.componentTopK ?? 18,
        minScore: minScore * 0.85,
        reason: "component fallback supports missing ui-blocks",
      },
    ];
  }
  if (!topBlock || topBlock.score < minScore) {
    return [
      {
        kind: "page",
        topK: patch?.pageTopK ?? 3,
        minScore,
        reason: "page matched but ui-block ontology is weak",
      },
      {
        kind: "component",
        topK: patch?.componentTopK ?? 20,
        minScore: minScore * 0.8,
        reason: "compose missing ui-blocks from components",
      },
    ];
  }
  return [
    {
      kind: "page",
      topK: patch?.pageTopK ?? 3,
      minScore,
      reason: "page ontology matched",
    },
    {
      kind: "ui-block",
      topK: patch?.uiBlockTopK ?? 8,
      minScore,
      reason: "ui-block ontology matched",
    },
    {
      kind: "component",
      topK: patch?.componentTopK ?? 12,
      minScore: minScore * 0.8,
      reason: "component validation and repair scope",
    },
  ];
}

function buildDecisions(
  candidates: readonly OntologyMatchingCandidateScore[],
  assets: readonly OntologyMatchingAsset[],
  selected: OntologyMatchingSelectedAuthority,
  searchWindow: readonly OntologyMatchingSearchLevel[],
): OntologyMatchingDecision[] {
  const decisions: OntologyMatchingDecision[] = [];
  const assetById = new Map(assets.map((asset) => [asset.id, asset]));
  const windowByKind = new Map(
    searchWindow.map((entry) => [entry.kind, entry]),
  );
  const requiredPageIds = selected.requiredPageIds ?? [];
  const requiredBlockIds = selected.requiredUiBlockIds ?? [];
  const requiredComponentIds = selected.requiredComponentIds ?? [];
  const topPages = candidatesByKind(
    candidates,
    "page",
    windowByKind.get("page"),
  );
  const topBlocks = candidatesByKind(
    candidates,
    "ui-block",
    windowByKind.get("ui-block"),
  );
  const topComponents = candidatesByKind(
    candidates,
    "component",
    windowByKind.get("component"),
  );

  const selectedPageIds =
    requiredPageIds.length > 0
      ? requiredPageIds
      : topPages.slice(0, 1).map((candidate) => candidate.assetId);
  for (const pageId of selectedPageIds) {
    decisions.push(
      decideRequirement(pageId, "page", candidates, assetById, topBlocks),
    );
  }

  const requiredFromPages = selectedPageIds.flatMap(
    (pageId) => assetById.get(pageId)?.requiredChildIds ?? [],
  );
  const selectedBlockIds = unique([
    ...requiredBlockIds,
    ...requiredFromPages,
    ...topBlocks.slice(0, 2).map((candidate) => candidate.assetId),
  ]);
  for (const blockId of selectedBlockIds) {
    decisions.push(
      decideRequirement(
        blockId,
        "ui-block",
        candidates,
        assetById,
        topComponents,
      ),
    );
  }

  const requiredFromBlocks = selectedBlockIds.flatMap(
    (blockId) =>
      assetById.get(blockId)?.requiredComponents ??
      assetById.get(blockId)?.requiredChildIds ??
      [],
  );
  const selectedComponentIds = unique([
    ...requiredComponentIds,
    ...requiredFromBlocks,
  ]);
  for (const componentId of selectedComponentIds) {
    decisions.push(
      decideRequirement(componentId, "component", candidates, assetById, []),
    );
  }

  if (decisions.length === 0) {
    decisions.push({
      requirementId: "application",
      kind: "page",
      decision: "create_candidate",
      authority: "tool",
      score: 0,
      fallbackFrom: "ui-block",
      generationAllowed: false,
      candidateLifecycleStatus: "candidate",
      reasons: [
        "No page, ui-block, or component ontology candidate matched the intent.",
      ],
      evidenceRefs: ["matching:no-candidate"],
    });
  }

  return dedupeDecisions(decisions);
}

function decideRequirement(
  requirementId: string,
  kind: OntologyMatchingAssetKind,
  candidates: readonly OntologyMatchingCandidateScore[],
  assetById: ReadonlyMap<string, OntologyMatchingAsset>,
  fallbackChildren: readonly OntologyMatchingCandidateScore[],
): OntologyMatchingDecision {
  const exact = candidates.find(
    (candidate) =>
      candidate.kind === kind && candidate.assetId === requirementId,
  );
  const asset = assetById.get(requirementId);
  if (exact && exact.score >= REUSE_THRESHOLD) {
    return {
      requirementId,
      kind,
      decision: "reuse",
      authority: "tool",
      selectedAssetId: exact.assetId,
      score: exact.score,
      generationAllowed: true,
      candidateLifecycleStatus: "admitted",
      reasons: [`${kind} ${requirementId} matched above reuse threshold.`],
      evidenceRefs: [...(asset?.ontologyRefs ?? [`${kind}:${requirementId}`])],
    };
  }
  if (exact && exact.score >= ADAPT_THRESHOLD) {
    return {
      requirementId,
      kind,
      decision: "adapt",
      authority: "tool",
      selectedAssetId: exact.assetId,
      score: exact.score,
      generationAllowed: false,
      candidateLifecycleStatus: "adaptation-required",
      reasons: [
        `${kind} ${requirementId} matched but must route to candidate admission until visible/conformance oracle proof passes.`,
      ],
      evidenceRefs: [...(asset?.ontologyRefs ?? [`${kind}:${requirementId}`])],
    };
  }
  if (kind !== "component" && fallbackChildren.length > 0) {
    const childKind: OntologyMatchingAssetKind =
      kind === "page" ? "ui-block" : "component";
    return {
      requirementId,
      kind,
      decision: "compose",
      authority: "tool",
      selectedAssetId: fallbackChildren[0]?.assetId,
      score: fallbackChildren[0]?.score ?? 0,
      fallbackFrom: childKind,
      generationAllowed: true,
      candidateLifecycleStatus: "composed",
      reasons: [
        `${kind} ${requirementId} is composed from ${childKind} ontology candidates.`,
      ],
      evidenceRefs: fallbackChildren
        .slice(0, 4)
        .map((candidate) => `${candidate.kind}:${candidate.assetId}`),
    };
  }
  return {
    requirementId,
    kind,
    decision: "create_candidate",
    authority: "tool",
    score: exact?.score ?? 0,
    fallbackFrom:
      kind === "page"
        ? "ui-block"
        : kind === "ui-block"
          ? "component"
          : undefined,
    generationAllowed: false,
    candidateLifecycleStatus: "candidate",
    reasons: [
      `${kind} ${requirementId} is missing from admitted ontology and must enter candidate backfill.`,
    ],
    evidenceRefs: [`candidate:${kind}:${requirementId}`],
  };
}

function buildActionFlowImpact(
  decisions: readonly OntologyMatchingDecision[],
  assets: readonly OntologyMatchingAsset[],
): OntologyMatchingActionFlowImpact {
  const assetById = new Map(assets.map((asset) => [asset.id, asset]));
  const impacted = decisions.filter(
    (decision) =>
      decision.decision === "adapt" ||
      decision.decision === "compose" ||
      decision.decision === "create_candidate",
  );
  const impactedAssetIds = impacted.map((decision) => decision.requirementId);
  const actionBearing = impacted.some((decision) => {
    const asset = assetById.get(decision.requirementId);
    return (
      decision.kind !== "component" || Boolean(asset?.actionOutputs?.length)
    );
  });
  const requiresRebuild = impacted.some(
    (decision) =>
      decision.decision === "create_candidate" || decision.kind === "page",
  );
  return {
    required: impacted.length > 0 && actionBearing,
    mode:
      impacted.length === 0 || !actionBearing
        ? "none"
        : requiresRebuild
          ? "rebuild"
          : "patch",
    impactedAssetIds,
    requiredValidation:
      impacted.length === 0
        ? []
        : [
            "interaction-contract-recalculation",
            "compilerParamBindings",
            "validateActionFlowTopologyContract",
            "runtime-simulation",
            "browser-trace",
          ],
    reasons: impacted.map(
      (decision) => `${decision.requirementId}: ${decision.decision}`,
    ),
  };
}

function candidatesByKind(
  candidates: readonly OntologyMatchingCandidateScore[],
  kind: OntologyMatchingAssetKind,
  window: OntologyMatchingSearchLevel | undefined,
): OntologyMatchingCandidateScore[] {
  if (!window) return [];
  return candidates
    .filter(
      (candidate) =>
        candidate.kind === kind && candidate.score >= window.minScore,
    )
    .slice(0, window.topK);
}

function buildHardConstraints(
  selected: OntologyMatchingSelectedAuthority,
): string[] {
  return [
    ...(selected.designSystemId
      ? [`design-system:${selected.designSystemId}:hard`]
      : []),
    ...(selected.templateId ? [`template:${selected.templateId}:hard`] : []),
    ...(selected.requiredPageIds ?? []).map((id) => `required-page:${id}`),
    ...(selected.requiredUiBlockIds ?? []).map(
      (id) => `required-ui-block:${id}`,
    ),
    ...(selected.requiredComponentIds ?? []).map(
      (id) => `required-component:${id}`,
    ),
  ];
}

function compareCandidateScores(
  left: OntologyMatchingCandidateScore,
  right: OntologyMatchingCandidateScore,
): number {
  return (
    right.score - left.score ||
    left.kind.localeCompare(right.kind) ||
    left.assetId.localeCompare(right.assetId)
  );
}

function dedupeDecisions(
  decisions: readonly OntologyMatchingDecision[],
): OntologyMatchingDecision[] {
  const byKey = new Map<string, OntologyMatchingDecision>();
  for (const decision of decisions) {
    const key = `${decision.kind}:${decision.requirementId}`;
    const existing = byKey.get(key);
    if (
      !existing ||
      decisionRank(decision.decision) < decisionRank(existing.decision)
    ) {
      byKey.set(key, decision);
    }
  }
  return [...byKey.values()];
}

function decisionRank(decision: OntologyMatchingDecisionKind): number {
  switch (decision) {
    case "reuse":
      return 0;
    case "adapt":
      return 1;
    case "compose":
      return 2;
    case "create_candidate":
      return 3;
  }
}

function splitTerms(value: string): string[] {
  return tokenize(value.replace(/([a-z])([A-Z])/g, "$1 $2"));
}

function tokenize(value: string): string[] {
  return [
    ...new Set(
      value
        .toLowerCase()
        .replace(/[^a-z0-9가-힣_.:-]+/g, " ")
        .split(/\s+/)
        .flatMap((token) => token.split(/[-_:./]+/))
        .map(normalizeToken)
        .filter((token) => token.length > 1 && !GENERIC_STOP_WORDS.has(token)),
    ),
  ];
}

function normalizeToken(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9가-힣]+/g, "");
}

function unique<T>(values: readonly T[]): T[] {
  return [...new Set(values)];
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
